<template>
  <div class="ads-list-page">
    <div v-if="isLoading" class="text-center py-2">
      <b-spinner label="Loading pending ads..." />
    </div>
    <b-alert v-else-if="loadError" show variant="danger">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchPending">Retry</b-button>
    </b-alert>
    <div v-else-if="!rows.length" class="text-center text-muted py-3">
      No pending ads to review.
    </div>

    <b-card v-else no-body class="ads-table-card">
      <div class="ads-table-head">
        <div>ID</div>
        <div>Ads</div>
        <div>Company</div>
        <div />
      </div>

      <div
        v-for="(item, index) in rows"
        :key="`pending-${item.id}`"
        class="ads-table-row cursor-pointer"
        @click="openAdDetail(item)"
      >
        <div class="id-cell">{{ item.id }}</div>

        <div class="ads-cell">
          <b-img :src="item.adImage" class="ad-thumb" />
          <span class="ad-name">{{ item.adName }}</span>
        </div>

        <div class="company-cell">
          <b-avatar :src="item.companyLogo" size="34" class="company-avatar rounded-sm" />
          <span class="company-name">{{ item.companyName }}</span>
        </div>

        <div class="action-cell">
          <b-button
            variant="outline-secondary"
            class="list-action-btn mr-1"
            @click.stop="approveRow(item, index)"
          >
            <span class="action-icon-circle action-icon-circle--ok">
              <feather-icon icon="CheckIcon" size="14" class="action-icon-fg" />
            </span>
            Approve
          </b-button>
          <b-button
            variant="outline-secondary"
            class="list-action-btn"
            @click.stop="rejectRow(item, index)"
          >
            <span class="action-icon-circle action-icon-circle--no">
              <feather-icon icon="XIcon" size="14" class="action-icon-fg" />
            </span>
            Reject
          </b-button>
        </div>
      </div>
    </b-card>

    <AdminPaginationBar
      :page="currentPage"
      :per-page="perPage"
      :total="totalRows"
      @change="onPageChange"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BButton,
  BCard,
  BImg,
  BSpinner,
} from 'bootstrap-vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { applyListPagination, resolveMediaUrl, unwrapAdminResponse, extractApiErrorMessage } from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess, promptRejectReason } from '@/utils/uiActions'

export default {
  name: 'AdsPending',
  components: {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BImg,
    BSpinner,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      rows: [],
      actionInFlight: false,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
    }
  },
  created() {
    this.fetchPending()
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchPending()
    },
    mapRow(ad) {
      return {
        id: ad.id,
        adImage: resolveMediaUrl(ad.ad?.image),
        adName: ad.ad?.title || 'Untitled ad',
        companyLogo: resolveMediaUrl(ad.company?.logo),
        companyName: ad.company?.name || '—',
      }
    },
    openAdDetail(item) {
      this.$router.push({ name: 'ad-detail', params: { id: String(item.id) } })
    },
    async fetchPending() {
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.adsPending({
          page: this.currentPage,
          per_page: this.perPage,
        })
        const payload = unwrapAdminResponse(res)
        const list = payload.ads || []
        applyListPagination(this, res, payload)
        this.rows = list.map(a => this.mapRow(a))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load pending ads.'
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    approveRow(item, index) {
      if (this.actionInFlight) return
      confirmAction(
        this,
        { title: 'Approve ad?', text: `Approve "${item.adName}"?`, confirmButtonText: 'Approve' },
        async () => {
          if (this.actionInFlight) return
          this.actionInFlight = true
          try {
            await adminPanelApi.approveAd(item.id)
            this.rows = this.rows.filter((_, i) => i !== index)
            notifySuccess(this, 'Ad approved successfully.')
          } catch (e) {
            notifyError(this, extractApiErrorMessage(e, 'Unable to approve ad.'))
          } finally {
            this.actionInFlight = false
          }
        },
      )
    },
    async rejectRow(item, index) {
      if (this.actionInFlight) return
      const reason = await promptRejectReason(this, {
        title: 'Reject ad',
        text: `Provide a reason for rejecting "${item.adName}".`,
      })
      if (!reason) return

      this.actionInFlight = true
      try {
        await adminPanelApi.rejectAd({ id: item.id, reason })
        this.rows = this.rows.filter((_, i) => i !== index)
        notifySuccess(this, 'Ad rejected successfully.')
      } catch (e) {
        notifyError(this, extractApiErrorMessage(e, 'Unable to reject ad.'))
      } finally {
        this.actionInFlight = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.ads-table-card {
  border-radius: 10px;
  border: 1px solid #ecebf1;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.ads-table-head,
.ads-table-row {
  display: grid;
  grid-template-columns: 80px minmax(0, 1.7fr) minmax(0, 1fr) minmax(220px, 0.9fr);
  align-items: center;
}

.ads-table-head {
  padding: 0.7rem 1rem;
  background: #fbfbfd;
  border-bottom: 1px solid #ecebf1;
  color: #8d8fa1;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ads-table-row {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #ecebf1;
}

.ads-table-row:last-child {
  border-bottom: none;
}

.ads-cell,
.company-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.ad-thumb {
  width: 44px;
  height: 34px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.ad-name,
.company-name,
.id-cell {
  font-size: 0.95rem;
  color: #2f2b3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-avatar {
  margin-right: 0.65rem;
  flex-shrink: 0;
  border-radius: 8px !important;
}

::v-deep .company-avatar .b-avatar-img {
  border-radius: 8px;
}

.action-cell {
  display: flex;
  justify-content: flex-end;
}

.list-action-btn {
  border-color: #d8d6de;
  color: #5e5873;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.35rem 0.65rem;
}

.action-icon-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.35rem;
}

.action-icon-circle--ok {
  background: #28c76f;
}

.action-icon-circle--no {
  background: #ea5455;
}

.action-icon-fg {
  color: #fff;
}
</style>
